import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DmError, DmRefundClaim, dmRefundClaimList } from "@/services/firebase/artistHub";
import { DM_REFUND_PLATFORM_LABELS, DM_REFUND_STATUS_LABELS, DmRefundClaimStatus, refundErrorLabel } from "@/lib/dm";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const statusFilters: { value: DmRefundClaimStatus | ""; label: string }[] = [
  { value: "", label: "全部" },
  { value: "received", label: "待處理" },
  { value: "refunding", label: "退款處理中" },
  { value: "apple_guided", label: "已引導 Apple" },
  { value: "refunded", label: "已退款" },
];
const platformFilters: { value: DmRefundClaim["platform"] | ""; label: string }[] = [
  { value: "", label: "全部平台" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "coin", label: "金幣" },
];
const statusVariant = (s: string) => (s === "refunded" ? "success" : s === "apple_guided" ? "info" : "warning");

/**
 * C-03: 鐵粉私訊「30 天未收到訊息」退款申請（dmRefundClaims, B-29／B-33）. Staff only, read-only list.
 * dm* collections are server-only, so this calls the staff action `dmRefundClaimList` (requested from backend; not in
 * contract v2.3). Rendered as a section of 私訊審核 (DmModerationPage).
 */
export default function DmRefundClaimsPanel() {
  const [status, setStatus] = useState<DmRefundClaimStatus | "">("received");
  const [platform, setPlatform] = useState<DmRefundClaim["platform"] | "">("");
  const [artistInput, setArtistInput] = useState("");
  const [artistId, setArtistId] = useState("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const list = useQuery({
    queryKey: ["dm-refund-claims", status, platform, artistId, cursor],
    queryFn: () => dmRefundClaimList({ status: status || null, platform: platform || null, artistId: artistId || null, cursor }),
    retry: false,
  });
  const reset = () => setPages([null]);
  const notDeployed =
    list.error instanceof DmError && (list.error.status === 404 || (list.error.status === 400 && (list.error.code === "INVALID_ARGUMENT" || !list.error.code)));
  const claims = list.data?.claims ?? [];
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>私訊退款申請</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          鐵粉在本期 30 天內沒收到藝人本人訊息時可申請退款：iOS 引導至 Apple 回報問題頁；Android 由系統呼叫 Google Play 退款；金幣退回購買金幣。
          「處理中」且有錯誤代碼的申請需人工處理（例如 Google Play 後台手動退款）。
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2" role="group" aria-label="退款狀態">
            {statusFilters.map((f) => (
              <Button
                key={f.value || "all"}
                size="sm"
                variant={status === f.value ? "default" : "outline"}
                aria-pressed={status === f.value}
                onClick={() => {
                  setStatus(f.value);
                  reset();
                }}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div>
            <Label htmlFor="dm-refund-platform">平台</Label>
            <select
              id="dm-refund-platform"
              className="block rounded border bg-background px-2 py-2 text-sm"
              value={platform}
              onChange={(e) => {
                setPlatform(e.target.value as DmRefundClaim["platform"] | "");
                reset();
              }}
            >
              {platformFilters.map((p) => (
                <option key={p.value || "all"} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="dm-refund-artist">藝人 ID（選填）</Label>
            <Input id="dm-refund-artist" value={artistInput} placeholder="全部藝人" onChange={(e) => setArtistInput(e.target.value)} />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setArtistId(artistInput.trim());
              reset();
            }}
          >
            套用
          </Button>
          <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => void list.refetch()}>
            重新整理
          </Button>
        </div>
        {list.isError &&
          (notDeployed ? (
            <p role="status" className="text-sm text-muted-foreground">
              後端尚未提供 dmRefundClaimList（staff 列表 action），部署後即可使用。
            </p>
          ) : (
            <p role="alert" className="text-destructive">
              {errorText(list.error)}
            </p>
          ))}
        {list.isPending && <p>讀取退款申請…</p>}
        {list.data && claims.length === 0 && <p className="text-sm text-muted-foreground">沒有符合條件的退款申請。</p>}
        {claims.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-normal">狀態</th>
                  <th className="py-2 pr-3 font-normal">平台</th>
                  <th className="py-2 pr-3 font-normal">藝人</th>
                  <th className="py-2 pr-3 font-normal">粉絲 UID</th>
                  <th className="py-2 pr-3 font-normal">本期起點</th>
                  <th className="py-2 pr-3 font-normal">申請時間</th>
                  <th className="py-2 pr-3 font-normal">結案時間</th>
                  <th className="py-2 font-normal">錯誤</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {claims.map((c) => (
                  <tr key={c.id} title={c.id}>
                    <td className="py-2 pr-3">
                      <Badge variant={statusVariant(c.status)}>{DM_REFUND_STATUS_LABELS[c.status] ?? c.status}</Badge>
                    </td>
                    <td className="py-2 pr-3">{DM_REFUND_PLATFORM_LABELS[c.platform] ?? c.platform}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{c.artistId}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{c.uid}</td>
                    <td className="py-2 pr-3">{fmt(c.periodStart)}</td>
                    <td className="py-2 pr-3">{fmt(c.createdAt)}</td>
                    <td className="py-2 pr-3">{fmt(c.resolvedAt)}</td>
                    <td className="py-2 text-destructive">{refundErrorLabel(c.lastError)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {pages.length > 1 && (
            <Button size="sm" variant="outline" disabled={list.isFetching} onClick={reset}>
              回到最新
            </Button>
          )}
          {list.data?.nextCursor && (
            <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages((p) => [...p, list.data?.nextCursor ?? null])}>
              下一頁
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
